import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { createTenant } from '@/api/tenants';
import { updateUser } from '@/api/users';
// Legacy form component — kept until the TenantForm rewrite (see c12/c13 plans).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import TenantFormRaw from '@/components/admin/TenantForm';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TenantForm = TenantFormRaw as any;

interface TenantFormValues {
  name: string;
  area?: string | null;
  registration_number?: string | null;
  contact_info?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
}

export default function CreateFirstTenant() {
  const navigate = useNavigate();
  const { currentUser, refreshCurrentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: TenantFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: values.name,
        area: values.area || null,
        registration_number: values.registration_number || null,
        contact_info: values.contact_info || null,
        latitude:
          values.latitude !== '' && values.latitude != null
            ? parseFloat(String(values.latitude))
            : null,
        longitude:
          values.longitude !== '' && values.longitude != null
            ? parseFloat(String(values.longitude))
            : null,
      };
      const created = (await createTenant(payload)) as { id?: number };
      const newId = created?.id;

      if (currentUser?.id != null && newId != null) {
        try {
          await updateUser(currentUser.id, { tenant_id: newId });
        } catch (bindErr) {
          // Non-fatal: tenant exists; user can self-assign later.
          console.warn('Could not bind user to new tenant:', bindErr);
        }
      }
      if (refreshCurrentUser) await refreshCurrentUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Error creating first tenant:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.response?.data?.detail || e?.message || 'Failed to create tenant. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="container max-w-2xl py-10">
      <div className="mb-6 flex items-center gap-2">
        <Flame className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold tracking-tight">Welcome</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create your first FPA area</CardTitle>
          <CardDescription>
            Before you can use the dashboard, the system needs at least one
            FPA configured. You'll be linked to this area automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <TenantForm
            onSubmit={handleSubmit}
            onCancel={null}
            isSubmitting={submitting}
            error={error}
          />
        </CardContent>
      </Card>
    </div>
  );
}
